import React from 'react';
import { Helmet } from 'react-helmet-async';
import { FaBath, FaCheckCircle, FaArrowLeft, FaPhone } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import useScrollAnimation from '../../../hooks/useScrollAnimation';
import Footer from '../../layout/Footer';
import './ServicePage.css';

const BathroomRemodelingPage = () => {
  const navigate = useNavigate();
  const titleRef = useScrollAnimation();
  const featuresRef = useScrollAnimation();

  const features = [
    'Walk-in Shower Design & Installation',
    'Freestanding Tub Installation',
    'Custom Vanity & Storage Solutions',
    'Luxury Tile Work & Backsplashes',
    'Modern Fixture Installation',
    'Heated Flooring Systems',
    'Ventilation & Lighting Upgrades',
    'Accessibility Modifications'
  ];

  const processSteps = [
    {
      step: 1,
      title: 'Design Consultation',
      description: 'We assess your current bathroom and discuss your vision, needs, and budget for the renovation.'
    },
    {
      step: 2,
      title: 'Space Planning',
      description: 'Our designers create detailed layouts and 3D renderings to optimize your bathroom space.'
    },
    {
      step: 3,
      title: 'Material Selection',
      description: 'Choose from our premium selection of tiles, fixtures, and finishes to match your style.'
    },
    {
      step: 4,
      title: 'Professional Installation',
      description: 'Our certified craftsmen execute the renovation with precision and attention to detail.'
    },
    {
      step: 5,
      title: 'Quality Assurance',
      description: 'Final inspection and walkthrough to ensure everything meets our high standards.'
    }
  ];

  const scrollToContact = () => {
    navigate('/', { replace: true });
    setTimeout(() => {
      const contactSection = document.getElementById('contact');
      if (contactSection) {
        contactSection.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };

  return (
    <>
      <Helmet>
        <title>RC Roofing - Bathroom Remodeling</title>
        <meta name="description" content="Transform your bathroom with RC Roofing's luxury bathroom remodeling services. Walk-in showers, freestanding tubs, and custom vanities. Free estimates available." />
        <meta name="keywords" content="bathroom remodeling, bathroom renovation, walk-in shower, luxury bathroom, bathroom contractor, bathroom design" />
        <link rel="canonical" href="https://rcroofing.com/services/bathroom-remodeling" />
        
        {/* Open Graph */}
        <meta property="og:title" content="Bathroom Remodeling Services | RC Roofing" />
        <meta property="og:description" content="Transform your bathroom with RC Roofing's luxury bathroom remodeling services. Walk-in showers, freestanding tubs, and custom vanities." />
        <meta property="og:url" content="https://rcroofing.com/services/bathroom-remodeling" />
        <meta property="og:image" content="https://rcroofing.com/images/banner/hero2.webp" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Bathroom Remodeling Services | RC Roofing" />
        <meta name="twitter:description" content="Transform your bathroom with RC Roofing's luxury bathroom remodeling services. Walk-in showers, freestanding tubs, and custom vanities." />
        <meta name="twitter:image" content="https://rcroofing.com/images/banner/hero2.webp" />
      </Helmet>

      <div className="service-page">
        {/* Navigation */}
        <nav className="service-nav">
          <div className="container">
            <button 
              className="back-button"
              onClick={() => navigate('/')}
            >
              <FaArrowLeft /> Back to Home
            </button>
            <button 
              className="contact-button"
              onClick={scrollToContact}
            >
              <FaPhone /> Get Free Quote
            </button>
          </div>
        </nav>

        {/* Hero Section */}
        <section className="service-hero">
          <div className="service-hero-background">
            <img src="/images/banner/hero2.webp" alt="Professional bathroom remodeling by RC Roofing" />
            <div className="service-hero-overlay"></div>
          </div>
          <div className="container">
            <div className="service-hero-content">
              <div className="service-icon">
                <FaBath />
              </div>
              <h1 ref={titleRef} className="service-hero-title">
                Bathroom Remodeling Services
              </h1>
              <p className="service-hero-description">
                Create your personal spa retreat with our luxury bathroom remodeling services. 
                From elegant walk-in showers to stunning freestanding tubs, we design bathrooms 
                that combine comfort, style, and functionality.
              </p>
              <div className="service-hero-stats">
                <div className="stat">
                  <strong>150+</strong>
                  <span>Bathrooms Remodeled</span>
                </div>
                <div className="stat">
                  <strong>3-5 weeks</strong>
                  <span>Typical Timeline</span>
                </div>
                <div className="stat">
                  <strong>2 years</strong>
                  <span>Workmanship Warranty</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="service-features">
          <div className="container">
            <h2>What's Included in Our Bathroom Remodeling</h2>
            <div ref={featuresRef} className="features-grid">
              {features.map((feature, index) => (
                <div key={index} className="feature-item">
                  <FaCheckCircle className="feature-icon" />
                  <span>{feature}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Process Section */}
        <section className="service-process">
          <div className="container">
            <h2>Our Bathroom Remodeling Process</h2>
            <div className="process-steps">
              {processSteps.map((step, index) => (
                <div key={index} className="process-step">
                  <div className="step-number">{step.step}</div>
                  <div className="step-content">
                    <h3>{step.title}</h3>
                    <p>{step.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="service-cta">
          <div className="container">
            <div className="cta-content">
              <h2>Ready to Transform Your Bathroom?</h2>
              <p>
                Get a free, detailed estimate for your bathroom remodeling project. 
                Our team will help you create a luxury bathroom that fits your lifestyle and budget.
              </p>
              <div className="cta-buttons">
                <button className="btn btn-primary" onClick={scrollToContact}>
                  Get Free Estimate
                </button>
                <button className="btn btn-outline" onClick={() => navigate('/gallery')}>
                  View Our Work
                </button>
              </div>
            </div>
          </div>
        </section>
      </div>
      <Footer />
    </>
  );
};

export default BathroomRemodelingPage;
